import {
  Box,
  Collapse,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { primaryTint } from 'components/@home/@settings/Tabs/Agents/agenticThemeHelpers';
import { ChatFlowContext } from 'components/@home/@settings/Tabs/ChatFlowContext';
import ChatFlowEdit from 'components/@home/@settings/Tabs/ChatFlowEdit';
import OverflowTip from 'components/common/OverflowTip';
import orderBy from 'lodash/orderBy';
import AddIcon from 'mdi-react/AddIcon';
import ChevronDownIcon from 'mdi-react/ChevronDownIcon';
import ChevronRightIcon from 'mdi-react/ChevronRightIcon';
import { arrayOf, bool, func, object, string } from 'prop-types';
import React, { useContext, useMemo, useState } from 'react';
import { I18n } from 'utils/i18n';

const getStyles = theme => ({
  layout: {
    display: 'flex',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  nav: {
    width: 240,
    flexShrink: 0,
    borderRight: `1px solid ${theme.palette.divider}`,
    overflowY: 'auto',
    padding: theme.spacing(1, 0, 2),
    backgroundColor: theme.palette.background.paper,
  },
  sectionRow: {
    paddingLeft: theme.spacing(0.5),
    paddingRight: theme.spacing(0.5),
    minHeight: 36,
    borderRadius: '6px',
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    gap: theme.spacing(0.25),
    '&:hover .MuiIconButton-root': { opacity: 1 },
  },
  chevronSlot: {
    width: 22,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.text.secondary,
    flexShrink: 0,
  },
  sectionLabel: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 600,
    color: theme.palette.text.primary,
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  addBtn: {
    padding: '4px',
    color: theme.palette.text.secondary,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '6px',
    backgroundColor: theme.palette.background.paper,
    opacity: 0,
    transition: 'opacity 120ms ease, background-color 120ms ease, color 120ms ease',
    '&:hover': {
      color: theme.palette.primary.main,
      borderColor: theme.palette.primary.main,
      backgroundColor: primaryTint(theme, 0.06),
    },
  },
  subItem: {
    paddingLeft: theme.spacing(4.5),
    paddingRight: theme.spacing(1),
    minHeight: 30,
    borderRadius: '6px',
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  subLabel: {
    fontSize: theme.typography.pxToRem(12.5),
    color: theme.palette.text.primary,
  },
  emptyHint: {
    paddingLeft: theme.spacing(4.5),
    paddingRight: theme.spacing(2),
    paddingTop: theme.spacing(0.25),
    paddingBottom: theme.spacing(0.5),
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.text.disabled,
    fontStyle: 'italic',
  },
  canvas: {
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
});

const Section = ({
  styles,
  label,
  expanded,
  onToggle,
  onAdd = null,
  items,
  emptyHintKey,
  isSelected,
  onSelect,
}) => (
  <>
    <ListItemButton sx={styles.sectionRow} onClick={() => onToggle(!expanded)}>
      <Box component="span" sx={styles.chevronSlot}>
        {expanded ? <ChevronDownIcon size={16} /> : <ChevronRightIcon size={16} />}
      </Box>
      <ListItemText primary={label} primaryTypographyProps={{ sx: styles.sectionLabel }} />
      {onAdd && (
        <Tooltip title={I18n.t('Common.Add')}>
          <IconButton
            size="small"
            sx={styles.addBtn}
            onClick={e => {
              e.stopPropagation();
              onAdd();
            }}
          >
            <AddIcon size={16} />
          </IconButton>
        </Tooltip>
      )}
    </ListItemButton>
    <Collapse in={expanded} unmountOnExit>
      {items.length === 0 ? (
        <Box sx={styles.emptyHint}>{I18n.t(emptyHintKey)}</Box>
      ) : (
        items.map(it => (
          <ListItemButton
            key={it._id}
            selected={isSelected(it)}
            sx={styles.subItem}
            onClick={() => onSelect(it)}
          >
            <ListItemText
              primary={<OverflowTip value={it.name} />}
              primaryTypographyProps={{ sx: styles.subLabel }}
            />
          </ListItemButton>
        ))
      )}
    </Collapse>
  </>
);

Section.propTypes = {
  styles: object.isRequired,
  label: string.isRequired,
  expanded: bool.isRequired,
  onToggle: func.isRequired,
  onAdd: func,
  items: arrayOf(object).isRequired,
  emptyHintKey: string.isRequired,
  isSelected: func.isRequired,
  onSelect: func.isRequired,
};

const PagesFlowShell = ({
  flow,
  chatFlowEntityTypes = {},
  chatFlowIntents = {},
  chatFlowIntegrations = {},
}) => {
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const { context } = useContext(ChatFlowContext);
  const [expanded, setExpanded] = useState({
    pages: true,
    entityTypes: false,
    intents: false,
    integrations: false,
  });

  const setExpand = (key, val) =>
    setExpanded(prev => ({ ...prev, [key]: typeof val === 'boolean' ? val : !prev[key] }));

  const pages = useMemo(
    () => orderBy(flow.pages || [], [p => (p.name || '').toLowerCase()]),
    [flow.pages],
  );

  const entityTypes = useMemo(
    () => orderBy(Object.values(chatFlowEntityTypes || {}), [e => (e.name || '').toLowerCase()]),
    [chatFlowEntityTypes],
  );

  const intents = useMemo(
    () => orderBy(Object.values(chatFlowIntents || {}), [i => (i.name || '').toLowerCase()]),
    [chatFlowIntents],
  );

  const integrations = useMemo(
    () => orderBy(Object.values(chatFlowIntegrations || {}), [i => (i.name || '').toLowerCase()]),
    [chatFlowIntegrations],
  );

  const current = context?.current;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <Box sx={styles.layout}>
        <Box sx={styles.nav} component="nav">
          <List disablePadding dense>
            <Section
              styles={styles}
              label={I18n.t('ChatFlowSettings.Pages') || 'Pages'}
              expanded={expanded.pages}
              onToggle={v => setExpand('pages', v)}
              onAdd={() => context?.set?.({ type: 'page', page: 'new' })}
              items={pages}
              emptyHintKey="ChatFlowSettings.Slots empty"
              isSelected={p => current?.type === 'page' && current?.page === p._id}
              onSelect={p => context?.set?.({ type: 'page', page: p._id })}
            />
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                px: 2,
                pt: 1.5,
                pb: 0.5,
                color: 'text.secondary',
                fontWeight: 600,
                fontSize: 11,
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              {I18n.t('ChatFlowSettings.Library') || 'Biblioteca'}
            </Typography>
            <Section
              styles={styles}
              label={I18n.t('ChatFlowSettings.Entity types')}
              expanded={expanded.entityTypes}
              onToggle={v => setExpand('entityTypes', v)}
              onAdd={() => context?.set?.({ type: 'entity-type', entityType: 'new' })}
              items={entityTypes}
              emptyHintKey="ChatFlowSettings.Slots empty"
              isSelected={e => current?.type === 'entity-type' && current?.entityType === e._id}
              onSelect={e => context?.set?.({ type: 'entity-type', entityType: e._id })}
            />
            <Section
              styles={styles}
              label={I18n.t('ChatFlowSettings.Intents') || 'Intents'}
              expanded={expanded.intents}
              onToggle={v => setExpand('intents', v)}
              onAdd={() => context?.set?.({ type: 'intent', intent: 'new' })}
              items={intents}
              emptyHintKey="ChatFlowSettings.Slots empty"
              isSelected={i => current?.type === 'intent' && current?.intent === i._id}
              onSelect={i => context?.set?.({ type: 'intent', intent: i._id })}
            />
            <Section
              styles={styles}
              label={I18n.t('ChatFlowSettings.Integrations') || 'Integrations'}
              expanded={expanded.integrations}
              onToggle={v => setExpand('integrations', v)}
              onAdd={() => context?.set?.({ type: 'integration', integration: 'new' })}
              items={integrations}
              emptyHintKey="ChatFlowSettings.Slots empty"
              isSelected={i => current?.type === 'integration' && current?.integration === i._id}
              onSelect={i => context?.set?.({ type: 'integration', integration: i._id })}
            />
          </List>
        </Box>
        <Box sx={styles.canvas}>
          <ChatFlowEdit flow={flow} />
        </Box>
      </Box>
    </Box>
  );
};

PagesFlowShell.propTypes = {
  flow: object.isRequired,
  chatFlowEntityTypes: object,
  chatFlowIntents: object,
  chatFlowIntegrations: object,
};

export default PagesFlowShell;
